import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";
import { BackButton } from "../shared/BackButton";
import { PhotoInput } from "../shared/PhotoInput";

const REPORT_TYPES = [
  {
    id: "hazard",
    label: "Hazard Report",
    icon: "⚠️",
    color: colors.red,
    description: "Report a serious safety hazard at a site",
  },
  {
    id: "system_offline",
    label: "System Offline",
    icon: "🔴",
    color: "#dc2626",
    description: "A fire system is not operational at this site",
  },
  {
    id: "fip_fault",
    label: "FIP Fault",
    icon: "⚡",
    color: "#ea580c",
    description: "Fire Indicator Panel is showing a fault",
  },
  {
    id: "parking",
    label: "Parking Note",
    icon: "🅿️",
    color: colors.blue,
    description: "Note parking access or issues at a site",
  },
  {
    id: "change",
    label: "Change Report",
    icon: "🔄",
    color: colors.amber,
    description: "Site is different than expected — layout, access, or occupancy changes",
  },
  {
    id: "service_note",
    label: "Service Note",
    icon: "📝",
    color: colors.teal,
    description: "Note to help improve future service at this site",
  },
  {
    id: "service_request",
    label: "Service Request",
    icon: "🔧",
    color: colors.slate,
    description: "Customer has requested something outside the normal scope",
  },
];

export function FieldTechReports() {
  const isMobile = useIsMobile();

  // View state: 'list' | 'new' | 'edit' | 'success'
  const [view, setView] = useState("list");
  const [myReports, setMyReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);

  // New report form state
  const [selectedType, setSelectedType] = useState(null);
  const [priority, setPriority] = useState("urgent");
  const [siteSearch, setSiteSearch] = useState("");
  const [siteResults, setSiteResults] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Edit report state
  const [editingReport, setEditingReport] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState("urgent");
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    fetchMyReports();
  }, []);

  const fetchMyReports = async () => {
    try {
      const res = await axios.get(`${API}/field-reports/`);
      setMyReports(res.data);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    } finally {
      setLoadingReports(false);
    }
  };

  // Site search for new report
  useEffect(() => {
    if (siteSearch.length < 2) { setSiteResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/sites/`);
        const q = siteSearch.toLowerCase();
        const filtered = res.data.filter(
          (s) =>
            s.site_name.toLowerCase().includes(q) ||
            s.site_address.toLowerCase().includes(q),
        );
        setSiteResults(filtered.slice(0, 8));
      } catch (err) {
        console.error("Site search error:", err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [siteSearch]);

  // PhotoInput hands over files already downscaled and re-encoded to JPEG.
  // That is not a nicety here: this path used to send the camera original,
  // and an iPhone library photo is usually HEIC, which the server rejects.
  const handlePhotoChange = (files) => {
    const file = files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const handleEditPhotoChange = (files) => {
    const file = files[0];
    if (!file) return;
    setEditPhoto(file);
    if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
    setEditPhotoPreview(URL.createObjectURL(file));
  };

  const removeEditPhoto = () => {
    setEditPhoto(null);
    if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview);
    setEditPhotoPreview(null);
  };

  const openEdit = (report) => {
    setEditingReport(report);
    setEditTitle(report.title || "");
    setEditDescription(report.description || "");
    setEditPriority(report.priority || "urgent");
    setEditPhoto(null);
    setEditPhotoPreview(null);
    setEditError("");
    setView("edit");
  };

  const closeEdit = () => {
    removeEditPhoto();
    setEditingReport(null);
    setView("list");
  };

  const resetForm = () => {
    setSelectedType(null);
    setPriority("urgent");
    setSiteSearch("");
    setSiteResults([]);
    setSelectedSite(null);
    setTitle("");
    setDescription("");
    removePhoto();
    setError("");
  };

  const handleSubmit = async () => {
    if (!selectedType) { setError("Please select a report type."); return; }
    if (!selectedSite) { setError("Please select a site."); return; }
    if (!description.trim()) { setError("Please enter a description."); return; }

    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("report_type", selectedType);
      fd.append("site", selectedSite.id);
      if (title.trim()) fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (selectedType === "service_request") fd.append("priority", priority);
      if (photo) fd.append("photo", photo);

      await axios.post(`${API}/field-reports/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchMyReports();
      resetForm();
      setView("success");
      setTimeout(() => setView("list"), 2500);
    } catch (err) {
      setError("Failed to submit report. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    if (!editDescription.trim()) { setEditError("Description is required."); return; }
    setEditSaving(true);
    setEditError("");
    try {
      const fd = new FormData();
      fd.append("title", editTitle.trim());
      fd.append("description", editDescription.trim());
      if (editingReport.report_type === "service_request") fd.append("priority", editPriority);
      if (editPhoto) fd.append("photo", editPhoto);

      const res = await axios.patch(`${API}/field-reports/${editingReport.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMyReports(prev => prev.map(r => r.id === editingReport.id ? res.data : r));
      closeEdit();
    } catch (err) {
      setEditError("Failed to save. Please try again.");
      console.error(err);
    } finally {
      setEditSaving(false);
    }
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "10px",
    padding: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: "10px",
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "700",
    color: colors.slate,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
    display: "block",
  };

  // Success screen
  if (view === "success") {
    return (
      <div style={{ padding: "60px", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: colors.teal }}>
          Report Submitted
        </div>
        <div style={{ color: colors.slate, marginTop: "8px" }}>
          Your report has been sent to management.
        </div>
      </div>
    );
  }

  // Edit report form
  if (view === "edit" && editingReport) {
    const t = REPORT_TYPES.find(x => x.id === editingReport.report_type);
    const isSR = editingReport.report_type === "service_request";
    const displayPhoto = editPhotoPreview || editingReport.photo_url;

    return (
      <div style={{ padding: isMobile ? "16px" : "28px", maxWidth: "600px", height: "100vh", overflowY: "auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
          <BackButton onClick={closeEdit} />
          <h1 style={{ fontSize: "20px", fontWeight: "700", color: colors.darkGrey, margin: 0 }}>
            Edit Report
          </h1>
        </div>

        {/* Report context (read-only) */}
        <div style={{ ...cardStyle, marginBottom: "20px", borderLeft: `4px solid ${t?.color || colors.slate}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontSize: "18px" }}>{t?.icon}</span>
            <span style={{ fontWeight: "700", fontSize: "14px", color: t?.color }}>{t?.label}</span>
          </div>
          <div style={{ fontWeight: "600", fontSize: "13px", color: colors.darkGrey }}>{editingReport.site_name}</div>
          <div style={{ fontSize: "11px", color: colors.slate }}>{editingReport.site_address}</div>
        </div>

        {/* Priority — service requests only */}
        {isSR && (
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Priority</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {[
                { id: "critical", label: "Critical", bg: "#fee2e2", color: "#b91c1c" },
                { id: "urgent", label: "Urgent", bg: "#fff7ed", color: "#c2410c" },
                { id: "please_do", label: "Please Do", bg: "#dbeafe", color: "#1d4ed8" },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setEditPriority(p.id)}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: "8px",
                    fontSize: "12px", fontWeight: "700", cursor: "pointer",
                    border: `2px solid ${editPriority === p.id ? p.color : colors.border}`,
                    backgroundColor: editPriority === p.id ? p.bg : colors.white,
                    color: editPriority === p.id ? p.color : colors.slate,
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Title */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Title (optional)</label>
          <input
            style={inputStyle}
            placeholder="Brief title..."
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={{ ...inputStyle, minHeight: "120px", resize: "vertical" }}
            placeholder="Describe what you observed..."
            value={editDescription}
            onChange={e => setEditDescription(e.target.value)}
          />
        </div>

        {/* Photo */}
        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Photo</label>
          {displayPhoto ? (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ position: "relative", display: "inline-block" }}>
                <a href={editPhotoPreview || editingReport.photo_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={displayPhoto}
                    alt="Report"
                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${colors.border}`, display: "block" }}
                  />
                </a>

              </div>
              {editPhotoPreview && (
                <button onClick={removeEditPhoto} style={{ fontSize: "12px", color: colors.red, background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>
                  Revert to original
                </button>
              )}
            </div>
          ) : (
            <PhotoInput onFiles={handleEditPhotoChange} />
          )}
        </div>

        {editError && (
          <div style={{ backgroundColor: "#fdecea", border: `1px solid ${colors.red}`, borderRadius: "6px", padding: "10px 14px", color: colors.red, fontSize: "13px", marginBottom: "16px" }}>
            {editError}
          </div>
        )}

        <button
          onClick={handleEditSave}
          disabled={editSaving}
          style={{
            width: "100%",
            backgroundColor: editSaving ? colors.slate : colors.teal,
            color: "white", border: "none", padding: "14px", borderRadius: "8px",
            fontSize: "15px", fontWeight: "700", cursor: editSaving ? "not-allowed" : "pointer",
            marginBottom: "60px",
          }}
        >
          {editSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    );
  }

  // New report form
  const isSR = selectedType === "service_request";
  const stepNum = (base) => isSR ? base + 1 : base;

  if (view === "new") {
    return (
      <div style={{ padding: isMobile ? "16px" : "28px", maxWidth: "600px", height: "100vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
          <BackButton onClick={() => { resetForm(); setView("list"); }} />
          <h1 style={{ fontSize: "20px", fontWeight: "700", color: colors.darkGrey, margin: 0 }}>
            New Report
          </h1>
        </div>

        {/* 1. Report type */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>1. Report Type</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {REPORT_TYPES.map((type) => (
              <div
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                style={{
                  ...cardStyle,
                  marginBottom: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  cursor: "pointer",
                  border: `2px solid ${selectedType === type.id ? type.color : colors.border}`,
                  backgroundColor: selectedType === type.id ? `${type.color}12` : colors.white,
                }}
              >
                <span style={{ fontSize: "22px" }}>{type.icon}</span>
                <div>
                  <div style={{ fontWeight: "600", fontSize: "14px", color: type.color }}>
                    {type.label}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.slate }}>
                    {type.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority — service requests only */}
        {isSR && (
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>2. Priority</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {[
                { id: "critical", label: "Critical", bg: "#fee2e2", color: "#b91c1c" },
                { id: "urgent", label: "Urgent", bg: "#fff7ed", color: "#c2410c" },
                { id: "please_do", label: "Please Do", bg: "#dbeafe", color: "#1d4ed8" },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setPriority(p.id)}
                  style={{
                    flex: 1, padding: "10px 0", borderRadius: "8px",
                    fontSize: "12px", fontWeight: "700", cursor: "pointer",
                    border: `2px solid ${priority === p.id ? p.color : colors.border}`,
                    backgroundColor: priority === p.id ? p.bg : colors.white,
                    color: priority === p.id ? p.color : colors.slate,
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Site search */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>{stepNum(2)}. Site</label>
          {selectedSite ? (
            <div style={{ ...cardStyle, marginBottom: 0, display: "flex", justifyContent: "space-between", alignItems: "center", border: `2px solid ${colors.teal}` }}>
              <div>
                <div style={{ fontWeight: "600", fontSize: "14px" }}>{selectedSite.site_name}</div>
                <div style={{ fontSize: "12px", color: colors.slate }}>{selectedSite.site_address}</div>
              </div>
              <button
                onClick={() => { setSelectedSite(null); setSiteSearch(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, fontSize: "18px" }}
              >
                ✕
              </button>
            </div>
          ) : (
            <div>
              <input
                style={inputStyle}
                placeholder="Search by site name or address..."
                value={siteSearch}
                onChange={(e) => setSiteSearch(e.target.value)}
              />
              {siteResults.length > 0 && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: "6px", marginTop: "4px", backgroundColor: colors.white, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                  {siteResults.map((site) => (
                    <div
                      key={site.id}
                      onClick={() => { setSelectedSite(site); setSiteSearch(""); setSiteResults([]); }}
                      style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${colors.border}`, fontSize: "14px" }}
                    >
                      <div style={{ fontWeight: "600" }}>{site.site_name}</div>
                      <div style={{ fontSize: "12px", color: colors.slate }}>{site.site_address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Title (optional) */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>{stepNum(3)}. Title (optional)</label>
          <input
            style={inputStyle}
            placeholder="Brief title for this report..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>{stepNum(4)}. Description</label>
          <textarea
            style={{ ...inputStyle, minHeight: "120px", resize: "vertical" }}
            placeholder="Describe what you observed or what needs attention..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Photo */}
        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>{stepNum(5)}. Photo (optional)</label>
          {photoPreview ? (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src={photoPreview}
                alt="Preview"
                style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${colors.border}` }}
              />
              <div>
                <div style={{ fontSize: "12px", color: colors.slate, marginBottom: "6px" }}>{photo?.name}</div>
                <button
                  onClick={removePhoto}
                  style={{ fontSize: "12px", color: colors.red, background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: "600" }}
                >
                  Remove photo
                </button>
              </div>
            </div>
          ) : (
            <PhotoInput onFiles={handlePhotoChange} />
          )}
        </div>

        {error && (
          <div style={{ backgroundColor: "#fdecea", border: `1px solid ${colors.red}`, borderRadius: "6px", padding: "10px 14px", color: colors.red, fontSize: "13px", marginBottom: "16px" }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: "100%",
            backgroundColor: submitting ? colors.slate : colors.teal,
            color: "white", border: "none", padding: "14px", borderRadius: "8px",
            fontSize: "15px", fontWeight: "700", cursor: submitting ? "not-allowed" : "pointer",
            marginBottom: "60px",
          }}
        >
          {submitting ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    );
  }

  // Report list view
  return (
    <div style={{ padding: isMobile ? "16px" : "28px", maxWidth: "700px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "20px", fontWeight: "700", color: colors.darkGrey, margin: 0 }}>
          Reports
        </h1>
        <button
          onClick={() => setView("new")}
          style={{
            backgroundColor: colors.teal, color: "white", border: "none",
            padding: "10px 18px", borderRadius: "8px", fontSize: "14px",
            fontWeight: "700", cursor: "pointer",
          }}
        >
          + New Report
        </button>
      </div>

      {loadingReports ? (
        <div style={{ color: "#999", textAlign: "center", padding: "40px" }}>Loading...</div>
      ) : myReports.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: "center", padding: "40px", color: "#999" }}>
          No reports submitted yet
        </div>
      ) : (
        myReports.map((report) => {
          const type = REPORT_TYPES.find((t) => t.id === report.report_type);
          const canEdit = !report.is_resolved;
          return (
            <div
              key={report.id}
              onClick={canEdit ? () => openEdit(report) : undefined}
              style={{
                ...cardStyle,
                borderLeft: `4px solid ${type?.color || colors.slate}`,
                cursor: canEdit ? "pointer" : "default",
                transition: "box-shadow 0.15s",
              }}
              onMouseEnter={canEdit ? e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)" : undefined}
              onMouseLeave={canEdit ? e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.08)" : undefined}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <span style={{ fontSize: "16px", marginRight: "8px" }}>{type?.icon}</span>
                  <span style={{ fontWeight: "700", fontSize: "14px", color: type?.color }}>
                    {type?.label || report.report_type}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {canEdit && (
                    <span style={{ fontSize: "10px", color: colors.teal, fontWeight: "600" }}>Tap to edit</span>
                  )}
                  <span style={{
                    fontSize: "11px",
                    backgroundColor: report.is_resolved ? "#e8f8f1" : "#fef9e7",
                    color: report.is_resolved ? colors.green : colors.amber,
                    padding: "3px 8px", borderRadius: "12px", fontWeight: "600",
                  }}>
                    {report.is_resolved ? "Resolved" : "Open"}
                  </span>
                </div>
              </div>
              <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey, marginTop: "8px" }}>
                {report.site_name}
              </div>
              <div style={{ fontSize: "12px", color: colors.slate }}>{report.site_address}</div>
              {report.title && (
                <div style={{ fontSize: "13px", color: colors.slate, marginTop: "6px", fontStyle: "italic" }}>
                  {report.title}
                </div>
              )}
              <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginTop: "6px" }}>
                <div style={{ flex: 1, fontSize: "13px", color: colors.slate }}>
                  {report.description}
                </div>
                {report.photo_url && (
                  <a href={report.photo_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                    <img
                      src={report.photo_url}
                      alt="Report"
                      style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${colors.border}`, flexShrink: 0 }}
                    />
                  </a>
                )}
              </div>
              <div style={{ fontSize: "11px", color: "#aaa", marginTop: "8px" }}>
                {new Date(report.submitted_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
